import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ListStatus } from "@/enum/list.enum";
import { WebhookType } from "@/enum/webhook.enum";
import { ROUTE_CONFIG } from "@/routes/config";

import { Webhook, WebhookAttachedList } from "../schema/webhook";

/** Lists shown before "+ N more". */
export const WEBHOOK_LISTS_SHOWN = 3;

/**
 * The label key of each trigger: the same words as the list settings' "Send leads to a webhook"
 * dropdown. A closed map, so a new trigger without a label fails a spec instead of rendering its key.
 */
export const WEBHOOK_TRIGGER_LABEL_KEYS: Record<WebhookType, string> = {
	[WebhookType.WEBHOOK_ALL_QUALIFIED_LEADS]: "list.settings.WEBHOOK_ALL_QUALIFIED_LEADS",
	[WebhookType.WEBHOOK_ALL_LEADS]: "list.settings.WEBHOOK_ALL_LEADS",
	[WebhookType.WEBHOOK_ALL_LEADS_INCLUDING_NOT_FOUND]:
		"list.settings.WEBHOOK_ALL_LEADS_INCLUDING_NOT_FOUND",
	[WebhookType.WEBHOOK_WHEN_ASSIGNED]: "list.settings.WEBHOOK_WHEN_ASSIGNED",
	[WebhookType.WEBHOOK_WHEN_REPLIED]: "list.settings.WEBHOOK_WHEN_REPLIED",
};

export const webhookTriggerLabelKey = (trigger: string): string =>
	WEBHOOK_TRIGGER_LABEL_KEYS[trigger as WebhookType] ?? "webhookLists.triggerUnknown";

const CHIP = "rounded-full px-2 py-0.5 text-xs whitespace-nowrap bg-status-info-light";

const TriggerChip: FunctionComponent<{ trigger: string }> = ({ trigger }) => {
	const { t } = useTranslation();

	return <span className={`${CHIP} text-text-meta`}>{t(webhookTriggerLabelKey(trigger))}</span>;
};

/** One attached list: its name links to its settings, where the toggle and the trigger live. */
const AttachedListRow: FunctionComponent<{ list: WebhookAttachedList }> = ({ list }) => {
	const { t } = useTranslation();

	return (
		<div className="flex items-center gap-2 whitespace-nowrap" data-test="webhook-list">
			<Link
				className="text-brand-light hover:underline cursor-pointer"
				to={`${ROUTE_CONFIG.LISTS}/create/settings/${list.listId}`}
			>
				{list.name}
			</Link>
			<TriggerChip trigger={list.trigger} />
			{list.listStatus !== ListStatus.ACTIVE && (
				<span className={`${CHIP} text-text-tertiary`}>{t("webhookLists.listInactive")}</span>
			)}
		</div>
	);
};

/** "Not attached to any list" and where to turn it on, named by the list settings' own label. */
const NotAttached: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col items-start gap-1 max-w-[300px]" data-test="webhook-not-attached">
			<span className="rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-status-warning-light text-text">
				{t("webhookLists.notAttached")}
			</span>
			<span className="text-xs text-text-meta">
				{t("webhookLists.notAttachedHint", { place: t("list.settings.sendLeadsToWebhook") })}
			</span>
		</div>
	);
};

/**
 * Where a webhook receives leads from (PREC-4253). A webhook only fires for the lists it is
 * attached to, and until now nothing on this page said which, so one attached to nothing looked
 * exactly like one that worked. A colleague's private list is counted, never named (PREC-3531).
 */
export const WebhookListsCell: FunctionComponent<{ webhook: Webhook }> = ({ webhook }) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);

	// An answer without the field says nothing about attachments; it must not read "not attached".
	if (!webhook.lists) {
		return null;
	}

	const privateCount = webhook.privateListCount ?? 0;

	if (webhook.lists.length === 0 && privateCount === 0) {
		return <NotAttached />;
	}

	const hidden = webhook.lists.length - WEBHOOK_LISTS_SHOWN;
	const shown = expanded ? webhook.lists : webhook.lists.slice(0, WEBHOOK_LISTS_SHOWN);

	return (
		<div className="flex flex-col gap-1">
			{shown.map((list) => (
				<AttachedListRow key={list.listId} list={list} />
			))}
			{privateCount > 0 && (
				<div
					className="flex items-center gap-2 whitespace-nowrap"
					data-test="webhook-private-lists"
				>
					<span className="text-text-meta">
						{t("webhookLists.privateLists", { count: privateCount })}
					</span>
					{(webhook.privateListTriggers ?? []).map((trigger) => (
						<TriggerChip key={trigger} trigger={trigger} />
					))}
				</div>
			)}
			{hidden > 0 && (
				<button
					className="text-xs text-text-meta hover:text-text w-fit cursor-pointer"
					data-test="webhook-lists-toggle"
					type="button"
					onClick={() => setExpanded((value) => !value)}
				>
					{expanded ? t("webhookLists.showLess") : t("webhookLists.showMore", { count: hidden })}
				</button>
			)}
		</div>
	);
};
